/**
 * The AI setting for each value in the current workspace (migration 920), and
 * the agents that can estimate values (the built-in Value Estimator and custom
 * agents made on it). DB-first read cache; writes go through valueAiService.
 */

import { create } from 'zustand';
import { fetchValueAiSettings } from '../../lib/valueAiService';
import { listCustomAgents } from '../../lib/customAgentService';
import { customAgentType, VALUE_ESTIMATOR_AGENT_TYPE } from '../../lib/ai/agentDefinitions';
import type { ValueAiSetting } from '../../lib/valueStatus';

export interface EstimatingAgent {
  agentType: string;
  name: string;
}

interface ValueAiSettingsState {
  workspaceId: string | null;
  settings: ValueAiSetting[];
  agents: EstimatingAgent[];
  loading: boolean;
  load: (workspaceId: string, opts?: { force?: boolean }) => Promise<void>;
  settingFor: (attributeKey: string) => ValueAiSetting | null;
  agentName: (agentType: string | null | undefined) => string;
  put: (setting: ValueAiSetting) => void;
}

export const useValueAiSettingsStore = create<ValueAiSettingsState>()((set, get) => ({
  workspaceId: null,
  settings: [],
  agents: [{ agentType: VALUE_ESTIMATOR_AGENT_TYPE, name: 'Value Estimator' }],
  loading: false,

  load: async (workspaceId, opts) => {
    if (!opts?.force && get().workspaceId === workspaceId && !get().loading) return;
    set({ loading: true, workspaceId });
    const [settings, custom] = await Promise.all([
      fetchValueAiSettings(workspaceId).catch(() => [] as ValueAiSetting[]),
      listCustomAgents(workspaceId).catch(() => ({ data: null, error: null })),
    ]);
    if (get().workspaceId !== workspaceId) return;
    const estimating = (custom.data ?? [])
      .filter((a) => a.behaviorKind === 'estimate_value')
      .map((a) => ({ agentType: customAgentType(a.id), name: a.name }));
    set({
      settings,
      agents: [{ agentType: VALUE_ESTIMATOR_AGENT_TYPE, name: 'Value Estimator' }, ...estimating],
      loading: false,
    });
  },

  settingFor: (attributeKey) => get().settings.find((s) => s.attributeKey === attributeKey) ?? null,

  agentName: (agentType) =>
    !agentType ? '—' : get().agents.find((a) => a.agentType === agentType)?.name ?? agentType,

  put: (setting) =>
    set((s) => ({ settings: [...s.settings.filter((x) => x.attributeKey !== setting.attributeKey), setting] })),
}));
